import React, { useState } from 'react';
import OngsClient from "../api/OngsClient";
import Grid from "@mui/material/Grid2";
import Skeleton from "@mui/material/Skeleton";
import { useApiCall } from "~/single-page/hooks/useApiCall";
import { useParams } from 'react-router-dom';
import NoItemsMessage from "~/components/NoItemsMessage";
import { Stack, Box, Typography, Button, Link, Popover, List, ListItem, ListItemIcon, ListItemText, Snackbar, Alert } from "@mui/material";
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import TwitterIcon from '@mui/icons-material/Twitter';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import ActionsSlider from "~/components/ActionManagement/Slider";
import LinkButton from "~/components/LinkButton";
import ODSsCards from "~/components/ODSs/Cards";
import GalleryCards from "~/components/NGOs/GalleryCards";
import SocialMediaLinks from '~/components/NGOs/SocialMediaLinks';
import { useDonationModal } from '~/single-page/hooks/useDonationModal';
import { useVolunteerModal } from '~/single-page/hooks/useVolunteerModal';
import { APP_ROUTES } from "~/single-page/constants/routes";
import { useScrollToSection } from '~/single-page/hooks/useScrollToSection';

const LoadingSkeleton = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Skeleton variant="rectangular" height={118} />
    </Grid>
  </Grid>
);

const ActionButton = ({ variant, onClick, children, outlined }) => (
  <Button
    variant={variant}
    onClick={onClick}
    sx={{
      backgroundColor: outlined ? 'transparent' : '#294BB6',
      color: outlined ? '#294BB6' : 'white',
      borderColor: '#294BB6',
      '&:hover': {
        backgroundColor: outlined ? 'transparent' : '#294BB6',
        borderColor: '#294BB6',
        opacity: 0.8
      },
      width: { xs: '100%', sm: '270px' },
      px: 4,
      py: 1.5,
      textTransform: 'none',
      fontWeight: outlined ? 600 : 700
    }}
  >
    {children}
  </Button>
);

export default function NGODetailPage() {
  const { id } = useParams();
  useScrollToSection();
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const {
    data: ong = null,
    success,
    pending,
  } = useApiCall({
    apiCall: () => id ? OngsClient.ong(id) : Promise.reject('ID não fornecido'),
    init: true,
    deps: [id],
  });

  const { handleOpenDonationModal, DonationModalComponent } = useDonationModal();
  const { handleOpenVolunteerModal, VolunteerModalComponent } = useVolunteerModal();

  const handleShareClick = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const shareOpen = Boolean(shareAnchorEl);
  const shareId = shareOpen ? 'share-popover' : undefined;
  
  const currentUrl = window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    setSnackbarMessage('Link copiado para a área de transferência!');
    setSnackbarOpen(true);
    handleShareClose();
  };

  const handleShareFacebook = () => {
    // const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
    // window.open(url, '_blank', 'width=600,height=400');
    // handleShareClose();
    const facebookLink = ong?.facebook_link;

    if (facebookLink) {
      window.open(facebookLink, '_blank');
    } else {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
      window.open(url, '_blank', 'width=600,height=400');
    }
    handleShareClose();
  };

  const handleShareX = () => {
    const text = `Conheça ${ong.name} no Bora Impactar!`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(currentUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
    handleShareClose();
  };

  const handleShareWhatsApp = () => {
    const text = `Conheça ${ong.name} no Bora Impactar: ${currentUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    handleShareClose();
  };

  const handleShareInstagram = () => {
    const instagramUrl = ong.instagram_link ? ong.instagram_link : 'https://www.instagram.com/';
    
    window.open(instagramUrl, '_blank');
    handleShareClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (pending) return <LoadingSkeleton />;
  if (!success || !ong) return <NoItemsMessage itemName="ONG" gender="feminino" />;

  return (
    <Stack 
      spacing={4} 
      sx={{ 
        maxWidth: '100%', 
        px: 2, 
        py: 3,
        pt: 3,
        '& > :not(style) ~ :not(style)': {
          mr: 0
        }
      }}
    >
      {ong.cover_photo_url && (
        <Box sx={{ 
          position: 'relative',
          width: '100%',
          height: 150,
          mb: 6,
          marginTop: -3,  
          marginLeft: -2, 
          marginRight: -2 
        }}>
          <Box
            component="img"
            src={ong.cover_photo_url}
            alt={`Imagem de capa da ${ong.name}`}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </Box>
      )}

      <Box sx={{ position: 'relative', mt: ong.cover_photo_url ? -5 : 0 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: -2.125,
            position: 'relative',
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' },
            justifyContent: 'center',
            width: '100%',
            mb: 2,
            position: 'relative',
            top: ong.cover_photo_url ? -100 : -20,
            zIndex: 5
          }}>
            {ong.logo_photo_url && (
              <Box
                component="img"
                src={ong.logo_photo_url}
                alt={`Logo da ${ong.name}`}
                sx={{
                  width: 138,
                  height: 138,
                  borderRadius: 3,
                  objectFit: 'cover',
                  border: '3px solid white',
                  boxShadow: 2
                }}
              />
            )}
          </Box>

          <Box sx={{ 
            width: { xs: '100%', sm: 'auto' },
            textAlign: { xs: 'center', sm: 'left' },
            // mt: { xs: 5, sm: 0 },
            mt: {
              xs: ong.cover_photo_url ? (ong.logo_photo_url ? -10 : -4) : -1,
              sm: ong.cover_photo_url ? 0 : 8
            }, 
            // Para telas entre 600px e 770px - apenas quando não tem foto de capa
            '@media (min-width: 600px) and (max-width: 1000px)': {
              marginTop: !ong.cover_photo_url ? '2px !important' : undefined  // Aplicar somente se não tiver foto de capa
            }
          }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              {ong.name}
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 1
            }}>
              <Typography variant="subtitle1" color="text.secondary">
                Desde {ong.start_year}
              </Typography>
              
              <Button
                endIcon={<ShareIcon />}
                color="primary"
                size="small"
                onClick={handleShareClick}
                aria-describedby={shareId}
                sx={{ 
                  display: { xs: 'flex', sm: 'none' },
                  fontSize: '0.75rem',
                  py: 0.5,
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                Compartilhar
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 1
          }}>
            {ong.logo_photo_url && (
              <Box
                component="img"
                src={ong.logo_photo_url}
                alt={`Logo da ${ong.name}`}
                sx={{
                  width: 138,
                  height: 138,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid white',
                  boxShadow: 2,
                  marginTop: ong.cover_photo_url ? -11 : -3,
                  marginRight: ong.cover_photo_url ? 5 : 5
                }}
              />
            )}
            
            <Button
              endIcon={<ShareIcon />}
              color="primary"
              size="small"
              onClick={handleShareClick}
              aria-describedby={shareId}
              sx={{ mt: 1, display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Compartilhar
            </Button>
          </Box>
          
          <Popover
            id={shareId}
            open={shareOpen}
            anchorEl={shareAnchorEl}
            onClose={handleShareClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <List sx={{ width: 250, p: 0 }}>
              <ListItem button onClick={handleCopyLink}>
                <ListItemIcon>
                  <ContentCopyIcon />
                </ListItemIcon>
                <ListItemText primary="Copiar link" />
              </ListItem>
              <ListItem button onClick={handleShareFacebook}>
                <ListItemIcon>
                  <FacebookIcon sx={{ color: "#1877F2" }} />
                </ListItemIcon>
                <ListItemText primary="Facebook" />
              </ListItem>
              <ListItem button onClick={handleShareX}>
                <ListItemIcon>
                  <XIcon sx={{ color: "#000000" }} />
                </ListItemIcon>
                <ListItemText primary="X" />
              </ListItem>
              <ListItem button onClick={handleShareInstagram}>
                <ListItemIcon>
                  <InstagramIcon sx={{ color: "#E4405F" }} />
                </ListItemIcon>
                <ListItemText primary="Instagram" />
              </ListItem>
              <ListItem button onClick={handleShareWhatsApp}>
                <ListItemIcon>
                  <WhatsAppIcon sx={{ color: "#25D366" }} />
                </ListItemIcon>
                <ListItemText primary="WhatsApp" />
              </ListItem>
            </List>
          </Popover>
        </Box>
      </Box>

      <Box sx={{ mt: 4, mb: 2, px: { xs: 2, sm: 0 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2,
            mt: 0,
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            lineHeight: 1.5
          }}
        >
          Conheça mais sobre {ong.name.toLowerCase().startsWith('a') ? 'a' : 'o'} {ong.name}
        </Typography>

        <Typography 
          variant="body1" 
          sx={{ 
            mb: 5,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            lineHeight: 1.6
          }}
        >
          {ong.description}
        </Typography>
      </Box>
      
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'flex-start' },
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}
      >
        <ActionButton variant="contained" onClick={handleOpenDonationModal}>
          Quero doar
        </ActionButton>

        <ActionButton variant="outlined" outlined onClick={handleOpenVolunteerModal}>
          Quero ser um voluntário
        </ActionButton>
      </Box>
      
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Ações
        </Typography>
        <Box sx={{ width: "100%" }}>
          <ActionsSlider ong={ong} showPastStatus={true} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <LinkButton
            color="secondary"
            sx={{ width: "255px" }}
            size="large"
            to={APP_ROUTES.ACTION_MANAGEMENTS}
          >
            Ver todas
          </LinkButton>
        </Box>
      </Box>

      <Box>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Typography variant="h6">
            Objetivos para o Desenvolvimento Sustentável
          </Typography>
          <Link
            href="https://brasil.un.org/pt-br/sdgs"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              textDecoration: 'none',
              color: 'primary.main',
              fontSize: '0.875rem',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Saiba mais
          </Link>
        </Box>
        <ODSsCards odss={ong.sustainable_development_goals}/>
      </Box>

      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Galeria
        </Typography>
        <GalleryCards ong={ong} />
      </Box>
      {(ong.instagram_link || ong.facebook_link || ong.x_link || ong.site) && (
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            Acompanhe nossas redes sociais
          </Typography>
          <SocialMediaLinks 
            socialLinks={{
              instagram: ong.instagram_link,
              facebook: ong.facebook_link,
              x: ong.x_link,
              site: ong.site
            }} 
          />
        </Box>
      )}

      <DonationModalComponent ngo={ong} />
      <VolunteerModalComponent ngo={ong} />
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
